import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TeButton,
  TeButtonMinimal,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
  TeSegment,
  TeSegmentText,
} from '@topcon/tuicl-react';

import PageLayout from '../../../components/Page/PageLayout';
import EmptyState from '../../../components/ServiceAgreement/Common/EmptyState';
import CreateServiceAgreementCustomerModal from '../../../components/ServiceAgreement/Customer/CreateServiceAgreementCustomerModal';
import TerminateCancelServiceAgreementModal from '../../../components/ServiceAgreement/Customer/TerminateCancelServiceAgreementModal';
import { ServiceAgreement } from '../../../components/ServiceAgreement/Common/ServiceAgreementTypes';
import {
  mapRequestBusinessPartnerToServiceAgreement,
  mapServiceAgreementBody,
} from '../../../utils/serviceAgreement';
import { useListError } from '../../../hooks/useListError';
import { useListState } from '../../../hooks/useListState';
import { useVisibilityRefetch } from '../../../hooks/useVisibilityRefetch';
import ServerPagedTable, { ServerPagedTableColumn } from '../common/ServerPagedTable';
import AddressCell from '../common/cells/AddressCell';
import BusinessPartnerIconCell from '../common/cells/BusinessPartnerIconCell';
import CellText from '../common/cells/CellText';
import CountryCell from '../common/cells/CountryCell';
import ExpiresInCell from '../common/cells/ExpiresInCell';
import OrganizationNumberCell from '../common/cells/OrganizationNumberCell';
import ServiceTypeCell from '../common/cells/ServiceTypeCell';
import StatusBadgeCell from '../common/cells/StatusBadgeCell';
import { ServiceAgreementStatusEnum } from '../../../api/generated/pm';
import { useCreateRequestsList, useJoinRequestsList } from './requests.queries';

type RequestsTab = 'create' | 'join';

/**
 * Requests page (PRD 04, D1): Create requests (open proxy Business Partner
 * Requests) and Join requests (outgoing pending/declined agreements with
 * existing Customers), each its own server-paged table. The active tab lives
 * in `?tab=create|join` (default `create`, including a missing or unknown
 * value) so it survives Back, reload and deep links.
 */
function CustomerRequests() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: RequestsTab = searchParams.get('tab') === 'join' ? 'join' : 'create';

  const setTab = useCallback(
    (next: RequestsTab) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', next);
        return params;
      });
    },
    [setSearchParams]
  );

  const { page, setPage, limit, setLimit, searchInput, setSearchInput, search } = useListState({
    resetKey: tab,
  });

  const createQuery = useCreateRequestsList({ page, limit, search, enabled: tab === 'create' });
  const joinQuery = useJoinRequestsList({ page, limit, search, enabled: tab === 'join' });
  const activeQuery = tab === 'create' ? createQuery : joinQuery;

  useVisibilityRefetch(activeQuery.refetch);
  const isError = useListError(activeQuery.error);

  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  // Captured with the row so a tab change while the modal is open cannot flip
  // the delete endpoint: Create rows are proxies, Join rows are agreements.
  const [cancelTarget, setCancelTarget] = useState<{
    sa: ServiceAgreement;
    kind: 'proxy' | 'agreement';
  }>();

  const createItems = useMemo<ServiceAgreement[]>(
    () =>
      (createQuery.data?.items ?? []).map((request) =>
        mapServiceAgreementBody(mapRequestBusinessPartnerToServiceAgreement(request))
      ),
    [createQuery.data]
  );

  const joinItems = useMemo<ServiceAgreement[]>(
    () => (joinQuery.data?.items ?? []).map((agreement) => mapServiceAgreementBody(agreement)),
    [joinQuery.data]
  );

  const items = tab === 'create' ? createItems : joinItems;
  const total = activeQuery.data?.total ?? 0;

  const columns = useMemo<ServerPagedTableColumn<ServiceAgreement>[]>(() => {
    const cols: ServerPagedTableColumn<ServiceAgreement>[] = [
      {
        key: 'icon',
        header: '',
        compact: true,
        render: () => <BusinessPartnerIconCell />,
      },
      {
        key: 'companyName',
        header: t('customersTable.columnCompanyName'),
        minWidth: 200,
        render: (sa) => <CellText text={sa.customer?.name ?? ''} />,
      },
      {
        key: 'serviceType',
        header: t('customersTable.columnServiceType'),
        render: (sa) => <ServiceTypeCell agreementTerms={sa.serviceAgreementTerms} />,
      },
      {
        key: 'status',
        header: t('requestsTable.columnStatus'),
        render: (sa) => <StatusBadgeCell status={sa.computedServiceAgreementStatus} />,
      },
      {
        key: 'expiresIn',
        header: t('customersTable.columnExpiresIn'),
        justifyContent: 'flex-end',
        // CSSD-1648: "empty for declined", on both tabs.
        render: (sa) =>
          sa.serviceAgreementStatus === ServiceAgreementStatusEnum.DECLINED ? (
            <CellText text="" />
          ) : (
            <ExpiresInCell requestExpiresIn={sa.requestExpiresIn} />
          ),
      },
    ];

    if (tab === 'join') {
      cols.push({
        key: 'organizationNo',
        header: t('customersTable.columnOrganizationNumber'),
        headerInfoTooltip: t('customersTable.tooltipOrganizationNumber'),
        justifyContent: 'flex-end',
        render: (sa) => <OrganizationNumberCell extId={sa.customer?.extId} />,
      });
    }

    cols.push(
      {
        key: 'companyAddress',
        header: t('customersTable.columnCompanyAddress'),
        minWidth: 240,
        render: (sa) => <AddressCell address={sa.customer?.companyAddress} />,
      },
      {
        key: 'country',
        header: t('customersTable.columnCountry'),
        minWidth: 120,
        render: (sa) => <CountryCell countryCode={sa.customer?.companyAddress.country} />,
      },
      {
        key: 'actions',
        header: '',
        compact: true,
        justifyContent: 'flex-end',
        render: (sa) => (
          <>
            <TeContextMenuControl contextId={`requests-${sa.id}`}>
              <TeButtonMinimal icon="more_vert" variant="secondary" size="s" />
            </TeContextMenuControl>
            <TeContextMenu contextId={`requests-${sa.id}`} size="s">
              <TeContextMenuItem
                text={t('cancelAgreementModal.title')}
                textColor="text.error.primary"
                teclick={() =>
                  setCancelTarget({ sa, kind: tab === 'create' ? 'proxy' : 'agreement' })
                }
              />
            </TeContextMenu>
          </>
        ),
      }
    );

    return cols;
  }, [tab, t]);

  return (
    <PageLayout
      fullWidth
      title={t('requestsTable.title')}
      itemCount={activeQuery.isError ? undefined : total}
      rightAccessory={
        <TeSegment size="s" variant="secondary">
          <TeSegmentText
            text={t('requestsTable.tabCreate')}
            active={tab === 'create'}
            teclick={() => setTab('create')}
          />
          <TeSegmentText
            text={t('requestsTable.tabJoin')}
            active={tab === 'join'}
            teclick={() => setTab('join')}
          />
        </TeSegment>
      }
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      searchPlaceholder={t('requestsTable.search')}
      actions={
        <TeButton
          text={t('customers.add')}
          variant="primary"
          size="s"
          teclick={() => setAddCustomerOpen(true)}
        />
      }
    >
      <ServerPagedTable
        columns={columns}
        items={items}
        rowKey={(sa) => sa.id}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={setLimit}
        isLoading={activeQuery.isLoading}
        isFetching={activeQuery.isFetching}
        isError={isError}
        emptyState={
          <EmptyState
            kind={search ? 'noSearch' : 'noItems'}
            resource="customers"
            subset="requests"
            title={search ? undefined : t(`emptyState.customers.requests.${tab}.title`)}
          />
        }
      />

      {addCustomerOpen && (
        <CreateServiceAgreementCustomerModal
          open={addCustomerOpen}
          onClose={() => setAddCustomerOpen(false)}
        />
      )}

      {cancelTarget && (
        <TerminateCancelServiceAgreementModal
          serviceAgreement={cancelTarget.sa}
          kind={cancelTarget.kind}
          open={!!cancelTarget}
          onClose={() => setCancelTarget(undefined)}
        />
      )}
    </PageLayout>
  );
}

export default CustomerRequests;
